import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Twitter, Linkedin } from 'lucide-react';
import api from '../utils/api';

const Footer = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    // Load social media links
    const fetchSocialLinks = async () => {
      try {
        const response = await api.get('/social-media?active_only=true');
        setSocialLinks(response.data);
      } catch (error) {
        console.error('Error loading social media links:', error);
      }
    };
    fetchSocialLinks();
  }, []);

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: Facebook,
      instagram: Instagram,
      youtube: Youtube,
      twitter: Twitter,
      linkedin: Linkedin,
      tiktok: () => (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      )
    };
    const Icon = icons[platform];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  return (
    <footer className="bg-gray-900 text-white" data-testid="footer">
      {/* Fun Packing List Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/packliste" 
            className="flex items-center justify-center gap-2 text-white hover:text-amber-100 transition-colors group"
          >
            <span className="text-xl">🎒</span>
            <span className="font-medium">
              {language === 'de' 
                ? 'Noch nicht gepackt? Unsere Packliste rettet Leben (und Urlaube)!' 
                : 'Not packed yet? Our packing list saves lives (and vacations)!'}
            </span>
            <span className="text-xl group-hover:animate-bounce">👉</span>
          </Link>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-xl font-bold mb-4">Surfmotion</h3>
            <p className="text-gray-400 text-sm">
              {language === 'de'
                ? 'Dein Premium Wassersport-Center in Soma Bay, Ägypten.'
                : 'Your Premium Watersports Center in Soma Bay, Egypt.'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">{language === 'de' ? 'Schnelllinks' : 'Quick Links'}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/windsurfing" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.windsurfing')}
                </Link>
              </li>
              <li>
                <Link to="/kitesurfing" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.kitesurfing')}
                </Link>
              </li>
              <li>
                <Link to="/wingfoiling" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.wingfoiling')}
                </Link>
              </li>
              <li>
                <Link to="/prices" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.prices')}
                </Link>
              </li>
              <li>
                <a 
                  href="https://reservations.surfmotion.com/reservation" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {t('nav.booking')}
                </a>
              </li>
              <li>
                <Link to="/packliste" className="text-gray-400 hover:text-white transition-colors">
                  {language === 'de' ? 'Packliste' : 'Packing List'} 🎒
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4">{t('contact.info')}</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Surfmotion Windsurfschool Red Sea<br />Red Sea, Hurghada/Safaga, Egypt</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>+20 106 856 2062</span>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>peter@surfmotion.com</span>
              </li>
              <li className="text-xs pt-2">
                <div className="font-semibold text-gray-300 mb-1">{language === 'de' ? 'Öffnungszeiten:' : 'Opening Hours:'}</div>
                <div>Okt-Apr: 9-17 Uhr</div>
                <div>Mai-Sep: 9-18 Uhr</div>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="font-semibold mb-4">Social Media</h4>
            <div className="flex space-x-4">
              {socialLinks.length > 0 ? (
                socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label={link.platform}
                  >
                    {getPlatformIcon(link.platform)}
                  </a>
                ))
              ) : (
                // Fallback to default links if none configured
                <>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    data-testid="facebook-link"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    data-testid="instagram-link"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    data-testid="youtube-link"
                  >
                    <Youtube className="w-6 h-6" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
          <p>© {new Date().getFullYear()} Surfmotion Soma Bay. All rights reserved.</p>
          <div className="mt-2 flex justify-center space-x-4">
            <Link to="/impressum" className="hover:text-white transition-colors">
              {language === 'de' ? 'Impressum' : 'Legal Notice'}
            </Link>
            <span>|</span>
            <Link to="/datenschutz" className="hover:text-white transition-colors">
              {language === 'de' ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
            <span>|</span>
            <Link to="/admin" className="hover:text-white transition-colors">
              {t('nav.admin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;